const PDP_SOCIAL_PROOF_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Produces a stable unsigned hash without storing or transmitting visitor data.
 * @param {string} value
 * @returns {number}
 */
function hashPdpSocialProofValue(value) {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

class PdpSocialProof extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    const manifest = this.querySelector('[data-pdp-social-proof-personas]');
    const primaryImage = this.querySelector('[data-pdp-social-proof-primary]');
    const secondaryImage = this.querySelector('[data-pdp-social-proof-secondary]');
    const name = this.querySelector('[data-pdp-social-proof-name]');

    if (!(manifest instanceof HTMLScriptElement) || !(primaryImage instanceof HTMLImageElement)) return;
    if (!(secondaryImage instanceof HTMLImageElement) || !(name instanceof HTMLElement)) return;

    let personas;

    try {
      personas = JSON.parse(manifest.textContent || '[]');
    } catch (error) {
      return;
    }

    if (!Array.isArray(personas) || personas.length < 2) return;

    const women = personas.filter((persona) => persona.gender === 'female');
    const men = personas.filter((persona) => persona.gender === 'male');

    if (women.length === 0 || men.length === 0) return;

    const productId = this.dataset.productId || 'preview';
    const windowNumber = Math.floor(Date.now() / PDP_SOCIAL_PROOF_WINDOW_MS);
    const startsWithWoman = (hashPdpSocialProofValue(`${productId}:gender`) + windowNumber) % 2 === 0;
    const primaryPool = startsWithWoman ? women : men;
    const secondaryPool = startsWithWoman ? men : women;
    const primaryPersona = this.#getPersona(primaryPool, productId, windowNumber, 'primary');
    const secondaryPersona = this.#getPersona(secondaryPool, productId, windowNumber, 'secondary');

    if (!this.#isValidPersona(primaryPersona) || !this.#isValidPersona(secondaryPersona)) return;

    primaryImage.src = primaryPersona.image;
    secondaryImage.src = secondaryPersona.image;
    name.textContent = primaryPersona.name;
    this.dataset.initialized = 'true';
  }

  /**
   * Selects deterministically from a gender-specific pool.
   * @param {string} productId
   * @param {number} windowNumber
   * @param {Array<{name: string, gender: string, image: string}>} pool
   * @param {string} salt
   * @returns {{name: string, gender: string, image: string}}
   */
  #getPersona(pool, productId, windowNumber, salt) {
    const productOffset = hashPdpSocialProofValue(`${productId}:${salt}`) % pool.length;
    return pool[(productOffset + (windowNumber % pool.length)) % pool.length];
  }

  /**
   * @param {unknown} persona
   * @returns {persona is {name: string, gender: string, image: string}}
   */
  #isValidPersona(persona) {
    return (
      typeof persona === 'object' &&
      persona !== null &&
      typeof persona.name === 'string' &&
      (persona.gender === 'female' || persona.gender === 'male') &&
      typeof persona.image === 'string'
    );
  }
}

if (!customElements.get('pdp-social-proof')) {
  customElements.define('pdp-social-proof', PdpSocialProof);
}
